/**
 * 貨品列表頁(REQ-20260520 ERP Phase 1C)。
 *
 * 搜尋 / 類別 / 單位 / 頁碼受控於網址參數(REQ-20260820006)—— 原本是純 useState,
 * 點進某個貨品再返回時整份清單會重置成第一頁的空白條件,使用者得重篩一次。
 * 讀寫方式沿用 CustomerConsolePage / SupplierConsolePage 既有的 useSearchParams 模式。
 */
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useProducts } from '../features/products/api';
import { useReferences } from '../features/settings/api';

export function ProductListPage(): JSX.Element {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const page = Math.max(1, Number(searchParams.get('page')) || 1);
  const q = searchParams.get('q') ?? '';
  const categoryId = searchParams.get('categoryId') ?? '';
  const unitId = searchParams.get('unitId') ?? '';
  const pageSize = 25;

  /**
   * 篩選 / 頁碼寫回網址。空字串就從網址拿掉(等於「沒篩」);一律 `replace` ——
   * 在搜尋框打字不該一個字一筆瀏覽紀錄,不然「上一頁」得按幾十次才回得去。
   */
  const updateParams = (patch: Record<string, string>): void => {
    const next = new URLSearchParams(searchParams);
    for (const [key, value] of Object.entries(patch)) {
      if (value) next.set(key, value);
      else next.delete(key);
    }
    setSearchParams(next, { replace: true });
  };

  const { data: categories } = useReferences('product-categories', false);
  const { data: units } = useReferences('product-units', false);

  const { data, isLoading, isError, error } = useProducts({
    page,
    pageSize,
    sort: 'updatedAt',
    order: 'desc',
    ...(q.trim() ? { q: q.trim() } : {}),
    ...(categoryId !== '' ? { categoryId: Number(categoryId) } : {}),
    ...(unitId !== '' ? { unitId: Number(unitId) } : {}),
  });

  const rows = data?.data ?? [];
  const total = data?.meta.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  return (
    <section>
      <header className="mb-4 flex items-center justify-between">
        <h1 className="text-2xl font-semibold">貨品管理</h1>
        <Link
          to="/products/new"
          className="bg-blue-600 text-white px-3 py-1.5 rounded hover:bg-blue-700 text-sm"
        >
          + 新增貨品
        </Link>
      </header>

      <div className="flex flex-wrap gap-3 mb-4 bg-white p-3 rounded border items-end">
        <label className="grid text-sm">
          <span className="text-slate-600">搜尋</span>
          <input
            type="text"
            value={q}
            onChange={(e) => updateParams({ q: e.target.value, page: '' })}
            placeholder="編號 / 品名 / 規格 / 條碼"
            className="border rounded px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-blue-400 w-72"
          />
        </label>
        <label className="grid text-sm">
          <span className="text-slate-600">類別</span>
          <select
            value={categoryId}
            onChange={(e) => updateParams({ categoryId: e.target.value, page: '' })}
            className="border rounded px-2 py-1.5"
          >
            <option value="">全部</option>
            {(categories?.data ?? []).map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </label>
        <label className="grid text-sm">
          <span className="text-slate-600">單位</span>
          <select
            value={unitId}
            onChange={(e) => updateParams({ unitId: e.target.value, page: '' })}
            className="border rounded px-2 py-1.5"
          >
            <option value="">全部</option>
            {(units?.data ?? []).map((u) => (
              <option key={u.id} value={u.id}>
                {u.name}
              </option>
            ))}
          </select>
        </label>
      </div>

      {isLoading ? (
        <div role="status" className="text-slate-500">載入中...</div>
      ) : isError ? (
        <div role="alert" className="text-red-600">
          載入失敗:{error instanceof Error ? error.message : '未知錯誤'}
        </div>
      ) : (
        <>
          <table className="w-full bg-white shadow-sm rounded-md">
            <thead>
              <tr className="border-b text-left text-sm text-slate-600">
                <th className="px-4 py-2 w-24">編號</th>
                <th className="px-4 py-2">品名</th>
                <th className="px-4 py-2 w-32">規格</th>
                <th className="px-4 py-2 w-24">類別</th>
                <th className="px-4 py-2 w-16">單位</th>
                <th className="px-4 py-2 w-24 text-right">標準售價</th>
                <th className="px-4 py-2 w-32">預設廠商</th>
                <th className="px-4 py-2 w-32">最後更新</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-4 py-6 text-center text-slate-500 text-sm">
                    沒有符合的貨品
                  </td>
                </tr>
              ) : (
                rows.map((r) => (
                  <tr
                    key={r.id}
                    className="border-b hover:bg-blue-50 cursor-pointer text-sm"
                    onClick={() => navigate(`/products/${r.id}/edit`)}
                  >
                    <td className="px-4 py-2 font-mono text-xs">{r.code}</td>
                    <td className="px-4 py-2 font-medium">{r.name}</td>
                    <td className="px-4 py-2 text-slate-500 truncate max-w-xs">{r.spec ?? '—'}</td>
                    <td className="px-4 py-2 text-slate-500">{r.categoryName ?? '—'}</td>
                    <td className="px-4 py-2 text-slate-500">{r.unitName ?? '—'}</td>
                    <td className="px-4 py-2 text-right tabular-nums">{r.listPrice ?? '—'}</td>
                    <td className="px-4 py-2 text-slate-500 truncate">{r.defaultSupplierName ?? '—'}</td>
                    <td className="px-4 py-2 text-xs text-slate-500">
                      {new Date(r.updatedAt).toLocaleDateString('zh-TW')}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>

          <div className="flex justify-between items-center mt-4 text-sm">
            <span className="text-slate-500">共 {total} 筆</span>
            <div className="flex gap-1">
              <button
                type="button"
                disabled={page <= 1}
                onClick={() => updateParams({ page: String(Math.max(1, page - 1)) })}
                className="border rounded px-3 py-1 text-slate-700 hover:bg-slate-100 disabled:opacity-50"
              >
                上一頁
              </button>
              <span className="px-3 py-1">{page} / {totalPages}</span>
              <button
                type="button"
                disabled={page >= totalPages}
                onClick={() => updateParams({ page: String(Math.min(totalPages, page + 1)) })}
                className="border rounded px-3 py-1 text-slate-700 hover:bg-slate-100 disabled:opacity-50"
              >
                下一頁
              </button>
            </div>
          </div>
        </>
      )}
    </section>
  );
}
